<template>
  <div
    class="column no-wrap"
  >
    <legend-seats
      v-if="showHeader && !zoom"
      :seat-chart-configuration="seatChartConfiguration"
      :initial-selected-seats="initialSelectedSeats"
      :seat-chart="seatChart"
      :seat-preferences="seatPreferences"
    />

    <div
      ref="seat-chart-wrapper"
      v-dragscroll="!editMode"
      class="col seat-chart-wrapper no-wrap items-stretch relative-position"
      :class="{
        'available-mode': availableMode,
        'selecting-mode': !availableMode,
        'seat-swap-mode': seatSwapMode,
        'override': seatSwapMode && initialSelectedSeats && initialSelectedSeats.length,
        'show-scrollbars': zoom
      }"
      data-test-id="seat-chart"
    >
      <div
        v-if="showScreen"
        class="screen-wrapper text-responsive q-mx-auto relative-position"
        :class="{ zoom: zoom }"
        :style="{ 'fill-opacity': $q.dark.isActive ? 0.25 : 0.08 }"
      >
        <div
          class="text-h6 text-responsive absolute-center text-center text-uppercase"
          style="height: fit-content;"
          :style="{
            'font-size': `${fontSizeBase * 1.5}px`,
            'padding-bottom': `${fontSizeBase * 1.5}px`,
          }"
        >
          {{ screenName }}
        </div>
        <svg
          viewBox="3 0 460 50"
        >
          <use xlink:href="/seats.svg#screen" />
        </svg>
      </div>
      <div
        ref="seat-chart"
        class="seat-chart column no-wrap q-px-xs"
        :class="{ zoom: zoom }"
        @mousemove="handleMouseMove($event)"
      >
        <div
          v-for="row in computedSeatChart"
          :key="row[0].id"
          class="seat-chart-row row no-wrap"
        >
          <div
            v-if="!showSeatNumbers && !!getRowNumber(row)"
            class="seat-row-number row col-auto justify-center items-center text-responsive"
            :style="{ 'font-size': `${fontSizeBase}px` }"
          >
            {{ getRowNumber(row) }}
          </div>
          <div
            v-for="seat in row"
            :key="seat.id"
            class="seat"
            :class="{
              'cursor-pointer': seat.available,
              'social-distancing': seat['social-distancing'] && (!heatMapMode || heatMapMode === 'none'),
              'previous-selection': $_.includes(previousSeatNames, seat.name),
              'custom-seat-type': seat.customSeatType
            }"
            :style="$_.merge(heatMapStyles(seat.name) || {}, customSeatTypeStyles(seat) || {})"
            :data-test-id="`seat-chart-seat-${seat.available ? 'available' : seat.held ? 'held' : 'reserved'}`"
            @mousedown="handleDragStart($event, seat.id)"
            @mouseup="handleDragEnd($event, seat.id, seat)"
          >
            <q-tooltip v-if="$_.includes(previousSeatNames, seat.name)">
              Seat when watched {{ seatPreferences.last_seats_for_chart[seatChartId].movie_name }}
            </q-tooltip>

            <div
              class="seat-content column justify-center text-center"
              :class="[
                {
                  selected: isSeatSelected(seat),
                  reserved: seat.reserved,
                  cancelled: seat.cancelled,
                  unavailable: !seat.available,
                  source: isSeatSource(seat)
                },
                seat.seatType
              ]"
            >
              <q-icon
                v-if="showSeatIcon(seat)"
                :class=" {
                  'absolute selected-icon': isSeatSelected(seat),
                  'absolute-center': !isSeatSelected(seat),
                }"
                :style="{ top: isSeatSelected(seat) ? '4%' : undefined }"
                :color="seatIconColor(seat)"
                :name="seatIcon(seat)"
                :size="seatIconSize(seat)"
              />
              <svg :viewBox="viewbox(seat)">
                <use :xlink:href="'/seats.svg#' + seat.seatType" />
              </svg>
              <div
                v-if="(showSeatNumbers && (!hideUnavailableSeatNumbers || seat.available) && (!seatSwapMode || (seat.reserved) || seat.cancelled)) || isSeatSelected(seat)"
                class="row justify-center q-pt-xs"
              >
                <div
                  class="seat-text text-responsive"
                  :style="seatTextStyle(seat)"
                >
                  {{ seat.displayName || seat.name }}
                </div>
              </div>
              <div
                v-if="availableMode && seat.seatType !== SEAT_TYPES.AISLE && !!(seat.held || seat.heldSeatSectionName)"
                class="absolute-full row justify-center items-center"
              >
                <q-icon
                  class="col q-pb-sm"
                  name="lock"
                  data-test-id="seat-chart-held-seat-icon"
                  :color="(seat.reserved || !seat.held) ? '' : 'red'"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="editMode && dragging"
        style="position: absolute; border: dashed 2px #000; pointer-events: none;"
        :style="{
          top: `${draggingTop}px`,
          left: `${draggingLeft}px`,
          height: `${draggingHeight}px`,
          width: `${draggingWidth}px`
        }"
      />
    </div>
    <slot name="footer">
      <div
        v-if="showFooter"
        class="col-auto control-bar text-white text-bold q-pa-sm row justify-between overflow-hidden"
      >
        <legend-seats
          class="no-padding"
          :seat-chart-configuration="seatChartConfiguration"
          :initial-selected-seats="initialSelectedSeats"
          :seat-chart="seatChart"
          :seat-preferences="seatPreferences"
          button-only
        />
        <div v-if="error && !$consumer && !$register && !$kiosk">
          {{ selectionDescription }}
        </div>
        <q-btn
          flat
          padding="none"
          dense
          :icon="zoom ? 'zoom_out' : 'zoom_in'"
          :label="zoom ? $t('nav.actions.zoomOut') : $t('nav.actions.zoomIn')"
          @click="zoom = !zoom"
        />
      </div>
    </slot>
    <q-dialog
      v-model="errorMessageDialog"
    >
      <q-card class="q-pa-md">
        <q-card-section>
          <div class="text-h6">
            {{ selectionDescription }}
          </div>
        </q-card-section>

        <q-card-actions class="row justify-center">
          <q-btn
            label="OK"
            color="negative"
            :text-color="$getColor(currentSite, 'negative')"
            @click="dismissError"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>

import SeatChart, { SEAT_TYPES } from 'src/models/screen/SeatChart'
import LegendSeats from 'src/modules/admin/seat-chart/LegendSeats'
const sortAlphaNum = (a, b) => a.localeCompare(b, 'en', { numeric: true })

export default {
  name: 'Seats',
  components: { LegendSeats },
  props: {
    screenName: {
      type: String,
      required: false,
      default: 'Screen'
    },
    seatChart: {
      type: SeatChart,
      required: true
    },
    seatChartConfiguration: {
      type: Object,
      required: false,
      default () {
        return {}
      }
    },
    heatMapMode: {
      type: String,
      required: false,
      default: null
    },
    seatsToSelect: {
      type: Number,
      required: false,
      default: 0
    },
    hasMarathon: {
      type: Boolean,
      required: false,
      default: false
    },
    // eslint-disable-next-line vue/require-prop-types
    ticketTypeId: {
      required: false,
      default: false
    },
    initialSelectedSeats: {
      type: Array,
      required: false,
      default: null
    },
    editingSeats: {
      type: Array,
      required: false,
      default: null
    },
    showSeatNumbers: {
      type: Boolean,
      required: false,
      default: false
    },
    seatSwapMode: {
      type: Boolean,
      required: false,
      default: false
    },
    consolidateToCenter: {
      type: Boolean,
      required: false,
      default: false
    },
    originalSeats: {
      type: Array,
      required: false,
      default: null
    },
    sourceSeats: {
      type: Array,
      required: false,
      default: null
    },
    hideUnavailableSeatNumbers: {
      type: Boolean,
      required: false,
      default: false
    },
    editMode: {
      type: Boolean,
      required: false,
      default: false
    },
    availableMode: {
      type: Boolean,
      required: false,
      default: false
    },
    showHeader: {
      type: Boolean,
      required: false,
      default: true
    },
    showScreen: {
      type: Boolean,
      required: false,
      default: true
    },
    showFooter: {
      type: Boolean,
      required: false,
      default: true
    },
    seatPreferences: {
      type: Object,
      required: false,
      default () {
        return {
          y_ratio: 0.5
        }
      }
    },
    isFullScreen: {
      type: Boolean,
      required: false,
      default: false
    },
    containerWidth: {
      type: Number,
      required: false,
      default: null
    },
    emitSeatObjects: {
      type: Boolean,
      required: false,
      default: false
    },
    autoSelectSeats: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  emits: ['update:model-value', 'no-best-available', 'select-seat', 'edit-held-seats', 'reserved-seat-click', 'drag-selection', 'error'],

  data () {
    return {
      internalSelectedSeats: [],
      isMounted: false,
      zoom: false,
      width: null,
      mouseDown: false,
      dragging: false,
      draggingClientXStart: 0,
      draggingClientYStart: 0,
      draggingClientXEnd: 0,
      draggingClientYEnd: 0,
      dragStart: null,
      fontSizeBase: 12,
      errorMessageDialog: false
    }
  },
  computed: {
    seatChartId () {
      return this.seatChart.id
    },
    seatChartArray () {
      return this.seatChart.seatChart
    },
    seatColumnCount () {
      return this.seatChartArray[0].length
    },
    selectedSeats: {
      get () {
        return this.editingSeats || this.internalSelectedSeats
      },
      set (value) {
        if (this.editingSeats) {
          this.editingSeats = value
        } else {
          this.internalSelectedSeats = value
        }
      }
    },
    computedSeatChart () {
      const seatChart = _(this.seatChartArray).cloneDeep()

      // False means to show all
      if (this.ticketTypeId === false) return seatChart

      if (this.ticketTypeId) {
        _(seatChart).flatten().each(seat => {
          if (seat.reserveDuringMarathons && this.hasMarathon) {
            seat.available = false
            return
          }

          if (!seat.limitTicketTypes) {
            seat.available = false
            return
          }

          if (!_.includes(seat.ticketTypeIds, this.ticketTypeId)) {
            seat.available = false
          }
        })
      } else {
        _(seatChart).flatten().each(seat => {
          if (seat.reserveDuringMarathons && this.hasMarathon) {
            seat.available = false
            return
          }

          if (seat.limitTicketTypes) {
            seat.available = false
          }
        })
      }

      return seatChart
    },
    error () {
      let error = null

      if (this.selectedSeats.length < this.seatsToSelect) {
        error = null
      } else if (!this.seatSwapMode && !this.seatChartConfiguration.allowPartialCouchSale && this.seatChart.hasPartialCouchSaleError(this.selectedSeats)) {
        error = 'Partial couch sale not allowed'
      } else if (!this.seatSwapMode && !this.seatChartConfiguration.allowSeatOrphans && this.createsSeatOrphan()) {
        error = 'Selection not allowed, single seat created'
      } else if (!this.seatSwapMode && !this.seatChartConfiguration.allowGaps && this.isSeatGap()) {
        error = 'Seat gaps not allowed'
      }

      this.$emit('error', error)

      return error
    },
    draggingTop () {
      return Math.min(this.draggingClientYStart, this.draggingClientYEnd)
    },
    draggingLeft () {
      return Math.min(this.draggingClientXStart, this.draggingClientXEnd)
    },
    draggingHeight () {
      return Math.abs(this.draggingClientYStart - this.draggingClientYEnd)
    },
    draggingWidth () {
      return Math.abs(this.draggingClientXStart - this.draggingClientXEnd)
    },
    selectionComplete () {
      return this.selectedSeats.length === this.seatsToSelect && !this.error
    },
    seatsLeftToSelect () {
      return this.seatsToSelect - this.selectedSeats.length
    },
    selectedSeatsString () { return [...this.selectedSeats].sort(sortAlphaNum).join(', ') },
    selectionDescription () {
      if (this.error) {
        return this.error
      } else if (this.selectionComplete) {
        return this.selectedSeatsString
      } else {
        return (this.ticketTypeName ? this.ticketTypeName + ' - ' : '') + this.selectedSeats.length + ' of ' + this.seatsToSelect
      }
    },
    ticketTypeName () {
      return (this.ticketTypeId && _.find(this.ticketTypes.data, { id: this.ticketTypeId })?.name) || 'Regular'
    },
    seatsAvailable () { return _(this.computedSeatChart).flatten().some({ available: true }) },
    previousSeats () {
      if (!this.seatPreferences.last_seats_for_chart) return

      return this.seatPreferences.last_seats_for_chart[this.seatChartId]
    },
    previousSeatNames () {
      return this.previousSeats?.seat_names || []
    }
  },
  watch: {
    selectedSeats: {
      handler () {
        if (this.$customerDisplayHandler) this.$customerDisplayHandler.publish('selectedSeats', [this.selectedSeats], false, false)
        this.$emit('update:model-value', { selectedSeats: this.emitSeatObjects ? _.map(this.selectedSeats, this.seatObject) : this.selectedSeats, description: this.selectionDescription, complete: this.selectionComplete })
      },
      deep: true
    },
    async 'seatPreferences.y_ratio' () {
      if (this.autoSelectSeats && !this.seatSwapMode) {
        this.selectedSeats = []
        this.autoSelect()
      }
    },
    error: {
      handler () {
        if (this.error && (this.$consumer || this.$register || this.$kiosk)) {
          this.errorMessageDialog = true
        }
      },
      immediate: true
    }
  },
  configuration: ['ticketTypes', 'currentSite'],
  created () {
    _.extend(this, { SEAT_TYPES })
    this.setInternalSelectedSeats()

    if (this.$customerDisplayHandler) this.$customerDisplayHandler.subscribe('selectedSeats', 'seatChart', this.setSelectedSeats)
  },
  unmounted () {
    if (this.$customerDisplayHandler) this.$customerDisplayHandler.unsubscribe('selectedSeats', 'seatChart')
  },
  mounted () {
    this.isMounted = true
  },
  methods: {
    getRowNumber (row) {
      // Filter out seats with seatType 'aisle'
      const seats = _.filter(row, row => row.seatType !== SEAT_TYPES.AISLE)

      // Ensure there is at least one valid seat and return the first character of its name
      if (seats.length > 0 && seats[0].name) {
        if (isNaN(seats[0].name[1])) {
          return seats[0].name.slice(0, 2)
        } else {
          return seats[0].name[0]
        }
      }

      return null // Return null if no valid seat is found
    },
    setInternalSelectedSeats () {
      if (this.initialSelectedSeats) {
        this.internalSelectedSeats = _.clone(this.initialSelectedSeats)
      }
    },
    viewbox ({ seatType }) {
      if (seatType === SEAT_TYPES.COUCH_VERTICAL_EAST_CENTER || seatType === SEAT_TYPES.COUCH_VERTICAL_WEST_CENTER) {
        return '0 0 200 340'
      }

      return '0 0 200 200'
    },
    setSelectedSeats (publishedValue) {
      this.selectedSeats = publishedValue[0]
    },
    showSeatIcon (seat) {
      return this.isSeatSelected(seat) || seat.reserved || (!seat.available && seat.name && seat.name !== SEAT_TYPES.AISLE)
    },
    seatIcon (seat) {
      if (this.isSeatSelected(seat)) {
        return 'fas fa-check'
      } else if (this.seatSwapMode || seat.seatType === SEAT_TYPES.AISLE || seat.seatType.includes(SEAT_TYPES.TABLE)) {
        return
      } else if (seat.reserved) {
        return 'fas fa-user'
      } else if (!seat.available) {
        return seat.seatType === SEAT_TYPES.WHEELCHAIR ? 'fas fa-slash' : 'fas fa-user-slash'
      }
      return undefined
    },
    seatIconColor (seat) {
      if (this.isSeatSelected(seat)) {
        return this.$getColor(this.currentSite, 'seatSelected')
      } else if (seat.reserved) {
        return 'negative'
      } else {
        return 'responsive-grey-5'
      }
    },
    seatIconSize (seat) {
      const initialAssumedContainerWidth = 500
      const fontLabelSizeFactor = this.isSeatSelected(seat) ? 0.3 : 0.24

      const containerWidth = this.containerWidth || initialAssumedContainerWidth
      let fontSize = containerWidth / this.seatColumnCount * fontLabelSizeFactor
      if (this.zoom) {
        fontSize *= 2
      }
      return `${fontSize}px`
    },
    clearSelectedSeats () {
      this.selectedSeats = []
    },

    autoSelect () {
      if (!this.seatsAvailable) {
        this.$emit('no-best-available')
        return
      }

      while (this.seatsAvailable && this.seatsLeftToSelect > 0) {
        const bestSeat = this.bestAvailableSeat()

        if (!bestSeat) {
          this.$emit('no-best-available')
          return
        }

        this.selectSeat(bestSeat)
      }
    },

    bestAvailableSeat () {
      // First seat that has room for the entire party in the row
      let targetRow
      let targetCol

      // If first selection, target is center of auditorium
      // If this is our second selection, use center of selected seats
      const rowCount = this.computedSeatChart.length - 1
      const colCount = this.computedSeatChart[0].length
      if (this.selectedSeats.length) {
        targetRow = _.sumBy(this.selectedSeats, seatName => this.seatLocation(seatName).rowIndex) / this.selectedSeats.length
        targetCol = _.sumBy(this.selectedSeats, seatName => this.seatLocation(seatName).colIndex) / this.selectedSeats.length
      } else if (this.seatSwapMode && this.originalSeats?.length) {
        const rowIndexes = _(this.originalSeats).map(seatName => this.seatLocation(seatName).rowIndex).filter(index => index >= 0).value()
        const colIndexes = _(this.originalSeats).map(seatName => this.seatLocation(seatName).colIndex).filter(index => index >= 0).value()
        const originalSeatsExistOnNewSeatChart = !!rowIndexes.length && !!colIndexes.length
        targetRow = originalSeatsExistOnNewSeatChart
          ? _.sum(rowIndexes) / rowIndexes.length
          : rowCount * this.seatPreferences.y_ratio
        targetCol = originalSeatsExistOnNewSeatChart && !this.consolidateToCenter
          ? _.sum(colIndexes) / colIndexes.length
          : this.seatPreferences.x_ratio ? colCount * this.seatPreferences.x_ratio : colCount / 2
      } else {
        targetRow = rowCount * this.seatPreferences.y_ratio
        targetCol = this.seatPreferences.x_ratio ? colCount * this.seatPreferences.x_ratio : colCount / 2
      }

      const forwardDistanceBiasFactor = 1.5 // How much worse is it to move back a row than forward
      const rowDistanceBiasFactor = 1.5 // How much worse is it to be off one row instead of one column

      let bestSeat = null
      let bestDistance = null
      const bestByConsecutiveSeats = {} // '{count: { bestSeat:, bestDistance:}'
      _.each(this.computedSeatChart, (row, rowIndex) =>
        _.each(row, (seat, colIndex) => {
          if (!seat.available || seat.seatType === SEAT_TYPES.WHEELCHAIR || seat.confirmationRequired || _.includes(this.selectedSeats, seat.name)) return

          const rowDelta = rowIndex - targetRow
          const colDelta = colIndex - targetCol

          let rowDistance = rowDelta * rowDistanceBiasFactor
          const colDistance = colDelta

          if (rowDistance < 0) rowDistance *= forwardDistanceBiasFactor

          const distance = Math.sqrt(Math.pow(rowDistance, 2) + Math.pow(colDistance, 2))

          const consecutiveSeatCount = this.findConsecutiveSeatCount(seat)

          if (consecutiveSeatCount >= this.seatsLeftToSelect) {
            if (!bestSeat || distance < bestDistance) {
              bestSeat = seat
              bestDistance = distance
            }
          } else {
            const entry = bestByConsecutiveSeats[consecutiveSeatCount] ||= { bestSeat: null, bestDistance: null }
            if (!entry.bestSeat || distance < entry.bestDistance) {
              entry.bestSeat = seat
              entry.bestDistance = distance
            }
          }
        }))

      // Value is set if there is a seat where everyone fits
      if (bestSeat) {
        return bestSeat
      }

      // Will return a value if there is at lease so number of consecutive seats
      const maxConsecutiveSeats = _(bestByConsecutiveSeats).keys().max()
      if (maxConsecutiveSeats) {
        return bestByConsecutiveSeats[maxConsecutiveSeats].bestSeat
      }

      return null
    },
    centerSeatOfPreviousSelection () {
      const row = Math.round(_.sumBy(this.previousSeatNames, seatName => this.seatLocation(seatName).rowIndex) / this.previousSeatNames.length)
      const col = Math.round(_.sumBy(this.previousSeatNames, seatName => this.seatLocation(seatName).colIndex) / this.previousSeatNames.length)

      return this.seatChartArray[row][col]
    },
    selectPriorSeats () {
      if (_.includes(this.selectedSeats, this.centerSeatOfPreviousSelection().name)) {
        this.selectedSeats = []
        this.autoSelect()
      } else {
        this.selectSeat(this.centerSeatOfPreviousSelection())
      }
    },
    heatMapStyles (seatName) {
      if (this.heatMapMode === 'count') return this.countHeatMapStyles(seatName)

      if (this.heatMapMode === 'preference') return this.preferenceHeatMapStyles(seatName)
    },
    customSeatTypeStyles (seat) {
      if (seat.customSeatType && seat.customSeatTypeColor) {
        return {
          color: seat.customSeatTypeColor
        }
      }
    },
    countHeatMapStyles (seatName) {
      const heatMap = this.seatPreferences.seat_count_maps[this.seatChartId]

      if (!heatMap) return

      const value = heatMap[seatName]

      return {
        'background-color': this.positiveColor(value / 3, value / 3)
      }
    },
    preferenceHeatMapStyles (seatName) {
      const heatMap = this.seatPreferences.seat_preference_maps[this.seatChartId]

      if (!heatMap) return

      const value = heatMap[seatName]

      let color
      if (value < 0) {
        color = this.negativeColor(value * -1, value * -1)
      } else {
        color = this.positiveColor(value / 3, value / 1.5)
      }

      return {
        'background-color': color
      }
    },
    // both are 0 - 1
    negativeColor (value, certainty) {
      const minColor = 54
      const maxColor = 0
      return this.valueColor(value, minColor, maxColor, certainty)
    },
    positiveColor (value, certainty) {
      const minColor = 85
      const maxColor = 120
      return this.valueColor(value, minColor, maxColor, certainty)
    },
    valueColor (value, minColor, maxColor, opacity = 1, saturation = 65, luminocity = 40) {
      const m = maxColor - minColor
      const b = minColor
      const y = value * m + b
      const boundedColor = Math.min(Math.max(y, minColor), maxColor)
      return `hsl(${boundedColor}, ${saturation}%, ${luminocity}%, ${opacity})`
    },
    findConsecutiveSeatCount (seat) {
      const { colIndex, rowIndex } = this.seatLocation(seat.name)

      let width = 1
      let doneLeft = false
      let doneRight = false
      let col

      let seats = 1

      while (seats < this.seatsLeftToSelect && (!doneLeft || !doneRight)) {
        if (!doneRight) {
          col = colIndex + width
          if (col >= this.computedSeatChart[rowIndex].length) {
            doneRight = true
          } else if (!this.computedSeatChart[rowIndex][col].available) {
            doneRight = true
          } else if (this.computedSeatChart[rowIndex][col].seatType === SEAT_TYPES.WHEELCHAIR) {
            doneRight = true
          } else if (this.computedSeatChart[rowIndex][col].confirmationRequired) {
            doneRight = true
          } else if (_.includes(this.selectedSeats, this.computedSeatChart[rowIndex][col].name)) {
            // Do nothing
          } else {
            seats += 1
          }
        }

        if (!doneLeft) {
          col = colIndex - width
          if (col < 0) {
            doneLeft = true
          } else if (!this.computedSeatChart[rowIndex][col].available) {
            doneLeft = true
          } else if (this.computedSeatChart[rowIndex][col].seatType === SEAT_TYPES.WHEELCHAIR) {
            doneLeft = true
          } else if (this.computedSeatChart[rowIndex][col].confirmationRequired) {
            doneLeft = true
          } else if (_.includes(this.selectedSeats, this.computedSeatChart[rowIndex][col].name)) {
            // Do nothing
          } else {
            seats += 1
          }
        }

        width += 1
      }

      return seats
    },
    seatTextStyle (seat) {
      let styleData
      if (seat.seatType === SEAT_TYPES.WHEELCHAIR || seat.seatType.includes(SEAT_TYPES.TABLE)) {
        styleData = 'margin-top: 60%;'
      } else {
        styleData = this.$q.screen.gt.xs ? 'margin-top: 25%;' : 'margin-top: 20%;'
      }

      const initialAssumedContainerWidth = 500
      const fontLabelSizeFactor = 0.25
      const containerWidth = this.containerWidth || initialAssumedContainerWidth
      let fontSize = containerWidth / this.seatColumnCount * fontLabelSizeFactor

      if (this.zoom) {
        fontSize *= 2
      }

      styleData += `font-size: ${fontSize}px;`
      this.fontSizeBase = fontSize
      return styleData
    },

    requestConfirmation (seat, onConfirm) {
      this.$q.dialog({
        title: 'Are you sure?',
        style: 'width: auto',
        message: seat.confirmationMessage || `This is a ${seat.type} seat. Are you sure you want to select it?`,
        ok: {
          label: 'Select Seat',
          color: 'primary'
        },
        cancel: {
          label: 'Cancel',
          color: 'negative'
        },
        focus: 'cancel'
      }).onOk(() => {
        onConfirm?.()
      })
    },
    removeSelectedSeat (seatName) {
      this.selectedSeats = _.without(this.selectedSeats, seatName)
    },
    async selectSeat (seat) {
      if (this.editMode) {
        this.$emit('select-seat', seat.id || seat.name)
        return
      }

      if (this.dragging) return

      if (this.availableMode && seat.heldSeatSectionName) {
        this.$emit('edit-held-seats')
        return
      }

      // Can't select an unavailable reserved seat, silently ignore
      if (!seat.available && !this.isSeatSelected(seat)) return

      if (this.editMode) {
        this.$emit('select-seat', seat.id || seat.name)
        return
      }

      // Unselect if already selected
      if (this.isSeatSelected(seat)) {
        this.removeSelectedSeat(seat.name)
        return
      }

      // Check more than one selection left to do and select a block
      if (this.seatsLeftToSelect > 1) {
        if (seat.confirmationRequired && !this.$register) {
          this.requestConfirmation(seat, () => {
            this.blockSelect(seat, this.seatsLeftToSelect)
          })
        } else {
          this.blockSelect(seat, this.seatsLeftToSelect)
        }

        return
      // Or check for all already selected, move the entire block
      } else if (this.seatsLeftToSelect === 0) {
        if (seat.confirmationRequired && !this.$register) {
          this.requestConfirmation(seat, () => {
            this.selectedSeats = []
            this.blockSelect(seat, this.seatsLeftToSelect)
          })
        } else {
          this.selectedSeats = []
          this.blockSelect(seat, this.seatsLeftToSelect)
        }

        return
      }

      // Remove earlier selection if we have exceeded selection target
      if (this.selectionComplete) {
        this.selectedSeats.shift()
      }

      // Select seat
      if (seat.confirmationRequired && !this.$register) {
        this.requestConfirmation(seat, () => {
          this.selectedSeats.push(seat.name)
        })
      } else {
        this.selectedSeats.push(seat.name)
      }

      await this.$nextTick()
    },
    // Select all the remaining seats to be selected in the spot clicked on
    blockSelect (seat, seatsToSelect) {
      const initialError = this.error
      const initialSelection = [...this.selectedSeats]
      const originalSeat = seat
      const originalSeatsToSelect = seatsToSelect

      // Support shifting from selection one site left and right to see if this prevents an error
      for (const offset of [null, -1, 1, 0]) {
        let { colIndex, rowIndex } = this.seatLocation(originalSeat.name)

        if (offset !== null) {
          const offsetSeat = this.computedSeatChart[rowIndex] && this.computedSeatChart[rowIndex][colIndex + offset]

          if (!offsetSeat || !offsetSeat.available) continue

          colIndex += offset
          seat = offsetSeat
          this.selectedSeats = [...initialSelection]
          seatsToSelect = originalSeatsToSelect
        }

        this.selectedSeats.push(seat.name)
        seatsToSelect -= 1

        let width = 1
        let doneLeft = false
        let doneRight = false
        let col

        while (seatsToSelect > 0 && (!doneLeft || !doneRight)) {
          if (!doneLeft && seatsToSelect > 0) {
            col = colIndex - width
            if (col < 0) {
              doneLeft = true
            } else if (!this.computedSeatChart[rowIndex][col].available) {
              doneLeft = true
            } else if (this.computedSeatChart[rowIndex][col].seatType === SEAT_TYPES.WHEELCHAIR) {
              doneLeft = true
            } else if (_.includes(this.selectedSeats, this.computedSeatChart[rowIndex][col].name)) {
              // Do nothing
            } else {
              this.selectedSeats.push(this.computedSeatChart[rowIndex][col].name)
              seatsToSelect -= 1
            }
          }

          if (!doneRight && seatsToSelect > 0) {
            col = colIndex + width
            if (col >= this.computedSeatChart[rowIndex].length) {
              doneRight = true
            } else if (!this.computedSeatChart[rowIndex][col].available) {
              doneRight = true
            } else if (this.computedSeatChart[rowIndex][col].seatType === SEAT_TYPES.WHEELCHAIR) {
              doneRight = true
            } else if (_.includes(this.selectedSeats, this.computedSeatChart[rowIndex][col].name)) {
              // Do nothing
            } else {
              this.selectedSeats.push(this.computedSeatChart[rowIndex][col].name)
              seatsToSelect -= 1
            }
          }

          width += 1
        }

        // If there are not errors or there was an error before and we didn't improve things
        // stop here. If there is a newly introduced error, try again with an offset
        if (!this.error || this.error === initialError) return
      }
    },
    isSeatGap () {
      if (!this.seatChartArray) return

      if (this.selectedSeats.length < 2) return false

      // For each row look for seat selections that have available seats directly between them
      for (let index = 0; index < this.selectedSeats.length; index++) {
        const selectedSeat = this.selectedSeats[index]

        const { colIndex, rowIndex } = this.seatLocation(selectedSeat)

        if (!this.computedSeatChart[rowIndex]) {
          console.log(`Seat gap check out of bounds [${this.computedSeatChart.length} <= ${rowIndex}]`)
          return
        }

        // Guilty until proven innocent
        // Look left and right until we see a good condition. If we see a bad condition exit function.
        const maxWidth = 5 // Look up to 5 seats away. Basically allow a gap of 5 seats
        let width = 1
        let doneLeft = false
        let doneRight = false
        let col

        let foundAvailableLeft = false
        let foundAvailableRight = false

        while (width < maxWidth && (!doneLeft || !doneRight)) {
          if (!doneRight) {
            col = colIndex + width
            if (col >= this.computedSeatChart[rowIndex].length) {
              doneRight = true
            } else if (!this.computedSeatChart[rowIndex][col].available) {
              doneRight = true
            } else if (!foundAvailableRight && _.includes(this.selectedSeats, this.computedSeatChart[rowIndex][col].name)) {
              doneRight = true
            } else if (foundAvailableRight && _.includes(this.selectedSeats, this.computedSeatChart[rowIndex][col].name)) {
              // There is a gap!!
              return true
            } else if (this.computedSeatChart[rowIndex][col].available) {
              foundAvailableRight = true
            } else {
              // Keep plotting along
            }
          }

          if (!doneLeft) {
            col = colIndex - width
            if (col < 0) {
              doneLeft = true
            } else if (!this.computedSeatChart[rowIndex][col].available) {
              doneLeft = true
            } else if (!foundAvailableLeft && _.includes(this.selectedSeats, this.computedSeatChart[rowIndex][col].name)) {
              doneLeft = true
            } else if (foundAvailableLeft && _.includes(this.selectedSeats, this.computedSeatChart[rowIndex][col].name)) {
              // There is a gap!!
              return true
            } else if (this.computedSeatChart[rowIndex][col].available) {
              foundAvailableLeft = true
            } else {
              // Keep plotting along
            }
          }

          width += 1
        }
      }
    },
    createsSeatOrphan () {
      if (!this.seatChartArray) return

      // For each row look for seat selections that have exactly one seat available on one side
      const checkedSeats = []
      for (let index = 0; index < this.selectedSeats.length; index++) {
        const selectedSeat = this.selectedSeats[index]

        if (_.includes(checkedSeats, selectedSeat)) continue

        const { colIndex, rowIndex } = this.seatLocation(selectedSeat)

        if (!this.computedSeatChart[rowIndex]) {
          console.log(`Seat gap check out of bounds [${this.computedSeatChart.length} <= ${rowIndex}]`)
          return
        }

        let width = 1
        let doneLeft = false
        let doneRight = false
        let col

        let availableLeft = false
        let availableRight = false
        let exactlyOneAvailableLeft = false
        let exactlyOneAvailableRight = false

        while (!doneLeft || !doneRight) {
          if (!doneRight) {
            col = colIndex + width
            if (col >= this.computedSeatChart[rowIndex].length) {
              doneRight = true
            } else if (
              !this.computedSeatChart[rowIndex][col].available ||
              this.computedSeatChart[rowIndex][col].seatType === SEAT_TYPES.WHEELCHAIR ||
              this.computedSeatChart[rowIndex][col].seatType === SEAT_TYPES.COUCH_LEFT
            ) {
              doneRight = true
            } else if (!_.includes(this.selectedSeats, this.computedSeatChart[rowIndex][col].name)) {
              availableRight = true
              exactlyOneAvailableRight = !this.computedSeatChart[rowIndex][col + 1] || !this.computedSeatChart[rowIndex][col + 1].available
              doneRight = true
            } else {
              // Keep plotting along
            }

            if (this.computedSeatChart[rowIndex][col]) {
              checkedSeats.push(this.computedSeatChart[rowIndex][col].name)
            }
          }

          if (!doneLeft) {
            col = colIndex - width
            if (col < 0) {
              doneLeft = true
            } else if (
              !this.computedSeatChart[rowIndex][col].available ||
              this.computedSeatChart[rowIndex][col].seatType === SEAT_TYPES.WHEELCHAIR ||
              this.computedSeatChart[rowIndex][col].seatType === SEAT_TYPES.COUCH_RIGHT
            ) {
              doneLeft = true
            } else if (!_.includes(this.selectedSeats, this.computedSeatChart[rowIndex][col].name)) {
              availableLeft = true
              exactlyOneAvailableLeft = !this.computedSeatChart[rowIndex][col - 1] || !this.computedSeatChart[rowIndex][col - 1].available
              doneLeft = true
            } else {
              // Keep plotting along
            }
            if (this.computedSeatChart[rowIndex][col]) {
              checkedSeats.push(this.computedSeatChart[rowIndex][col].name)
            }
          }

          if (exactlyOneAvailableLeft && availableRight) return true

          if (exactlyOneAvailableRight && availableLeft) return true

          width += 1
        }
      }
    },
    seatLocation (seatName) {
      let colIndex
      const rowIndex = _.findIndex(this.seatChartArray, (row) => {
        colIndex = _.findIndex(row, (testSeat) => testSeat.name === seatName && testSeat.seatType !== SEAT_TYPES.AISLE)
        return colIndex !== -1
      })
      return {
        colIndex,
        rowIndex
      }
    },
    seatObject (seatName) {
      const { colIndex, rowIndex } = this.seatLocation(seatName)
      return _.clone(this.seatChartArray[rowIndex][colIndex])
    },
    seatLocations (seatName) {
      const seatLocations = []
      _.each(this.seatChartArray, (row, rowIndex) =>
        _.each(row, (seat, colIndex) => {
          if (seat.name === seatName && seat.seatType !== SEAT_TYPES.AISLE) {
            seatLocations.push({ colIndex, rowIndex })
          }
        }))
      return seatLocations
    },
    isSeatSelected (seat) {
      if (this.editMode) return _.includes(this.selectedSeats, seat.id)

      if (seat.seatType === SEAT_TYPES.AISLE) return

      return _.includes(this.selectedSeats, seat.name)
    },
    isSeatSource (seat) {
      return _.includes(this.sourceSeats, seat.name)
    },
    handleDragStart (event, dragSeatStart) {
      if (!dragSeatStart) return
      if (this.dragSeatStart) return

      this.mouseDown = true

      if (!this.editMode) return

      this.dragSeatStart = dragSeatStart

      const wrapper = this.seatChartBoundingRect()

      this.draggingClientXStart = event.clientX - (wrapper.x || 0)
      this.draggingClientYStart = event.clientY - (wrapper.y || 0)
    },
    handleDragEnd (_event, dragSeatEnd, seat) {
      this.mouseDown = false

      if (this.seatSwapMode && seat.reserved) {
        this.$emit('reserved-seat-click', seat.name)
      }

      if (!this.dragging) {
        this.selectSeat(seat)
        this.dragging = false
        this.dragSeatStart = null
        return
      }

      this.dragging = false

      if (!this.editMode) return

      if (!this.dragSeatStart || this.dragSeatStart === dragSeatEnd || dragSeatEnd === null) {
        this.dragSeatStart = null
        return
      }

      this.$emit('drag-selection', [this.dragSeatStart, dragSeatEnd])
      this.dragSeatStart = null
    },
    handleMouseMove (event) {
      if (!this.mouseDown) {
        return
      }

      this.dragging = true

      if (!this.editMode) return

      const wrapper = this.seatChartBoundingRect()

      this.draggingClientXEnd = event.clientX - (wrapper.x || 0)
      this.draggingClientYEnd = event.clientY - (wrapper.y || 0)
    },
    seatChartBoundingRect () {
      return this.$refs['seat-chart-wrapper'] && this.$refs['seat-chart-wrapper'].getBoundingClientRect()
    },
    clearSelection () {
      this.internalSelectedSeats = []
    },
    dismissError () {
      this.errorMessageDialog = false
      this.error = null
    }
  }
}

</script>
<style lang="scss" scoped>
  .seat-chart-wrapper, .control-bar {
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    padding: 15px;
    overflow: auto;
  }

  .control-bar {
    background-color: rgba(66,66,66,.6);
    z-index: 1;
  }

  .screen-wrapper {
    width: 85%;
    transition: width 0.5s;
    margin-left: auto;
    margin-right: auto;
  }
  .seat-chart {
    width: 100%;
    transition: width 0.5s;
  }
  .seat-chart.zoom, .screen-wrapper.zoom {
    width: 200%;
  }
  .seat-chart-row {
    width: auto;
  }
  .seat-row-number{
    margin-left: -10px;
    padding-right: 8px;
  }
  .seat {
    flex-grow: 1;
    flex-shrink: 1;
    width: 20px;
    fill-opacity: 0;
  }
  .seat-content {
    width: 100%;
    position: relative;
  }
  svg {
    width: 100%;
    height: auto;
  }
  .absolute-center{
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    margin: auto;
    transform: none;
  }
  .selected-icon:before {
    height: auto;
    display: block !important;
    position: absolute;
    margin: auto;
  }
  .selecting-mode {
    .seat {
      color: var(--q-seatAvailable);
      fill-opacity: 0;
    }

    .unavailable {
      color: #AAA;
      stroke-dasharray: 15;
      fill-opacity: 0.2;
    }
    .reserved, .unavailable.reserved {
      color: var(--q-negative);
      stroke-dasharray: 15;
    }
    .selected {
      color: var(--q-seatSelected);
      fill-opacity: 0.4;
    }
    .selected.aisle {
      fill: var(--q-seatSelected);

      border-width: 1px solid var(--q-seatSelected);
    }

    .selected-icon {
      margin-left: auto;
      margin-right: auto;
      left: 0;
      right: 0;
      border-radius: 50%;
      opacity: 1;
      padding: 5%;
      background-color: var(--q-seatSelected);
    }

  }

  .available-mode {
    .seat {
      color: var(--q-positive);
    }
    .reserved {
      color: var(--q-primary);
    }
    .unavailable:not(.reserved) {
      color: #AAA;
      stroke-dasharray: 15;
    }
  }
  .seat-swap-mode {
    .seat {
      color: #AAA;
      .selected {
        color: var(--q-seatSelected) !important;
      }
      .source {
        color: var(--q-info) !important;
      }
      .reserved {
        color: var(--q-positive);
        stroke-dasharray: 0;
      }
      .cancelled {
        color: var(--q-negative);
        stroke-dasharray: 15;
      }
    }
  }
  .seat-swap-mode:not(.override) {
    pointer-events: none;
  }
  .seat-text {
    font-weight: 800;
    text-align: center;
    position: absolute;
    top: 15%;
    left: 0;
    right: 0;
    bottom: 0;
    user-select: none;
  }
  .seat-content {
    padding: 20% 5%;
  }
  .seat-content.regular-seat {
    padding: 20% 10%;
  }

  .seat-content.couch-left, .seat-content.couch-corner-left {
    padding: 20% 0% 20% 10%;
  }

  .seat-content.couch-vertical-east-left {
    padding: 40% 10% 0% 10%;
  }

  .seat-content.couch-right, .seat-content.couch-corner-right {
    padding: 20% 10% 20% 0%;
  }

  .seat-content.couch-vertical-west-right {
    padding: 40% 10% 0% 10%;
  }

  .seat-content.couch-center {
    padding: 20% 0%;
  }

  .seat-content.couch-vertical-east-center {
    margin-top: -10%;
    margin-bottom: -20%;
    padding: 0%;
  }

  .seat-content.couch-vertical-west-center {
    margin-top: -10%;
    margin-bottom: -20%;
    padding: 0%;
  }

  .seat-content.table, .seat-content.table-left, .seat-content.table-right, .seat-content.table-center {
    padding: 30% 0 0 0;
  }

  .seat-content.table-left {
    padding: 30% 0 0 0;
  }

  .seat-content.table-right {
    padding: 30% 0 0 0;
  }

  .seat-content.table-center {
    padding: 30% 0 0 0;
  }

  .show-scrollbars {
    overflow: auto;
  }

  .social-distancing {
    opacity: 0.4
  }

  .previous-selection {
    border: dashed blue 2px;
    margin: -2px 0px -2px -2px;
    width: 24px;
  }

  .top-legend .previous-selection {
    width: 29px;
    height: 35px;
  }

  .legend .previous-selection {
    width: 38px;
    height: 44px;
  }

  .top-legend-seat-name {
    font-size: 1.5vh;
    font-weight: 500;
  }

</style>
